/**
 * Enhanced Real-Time Master Data Service
 * Provides cached versions with real-time sync for instant updates
 */

import { citiesService } from "./cities.service";
import { clientService } from "./clientService";
import { propertyTypeService } from "./propertyType.service";
import { CACHE_KEYS, realTimeCacheService } from "./realTimeCache.service";
import { realTimeSyncService } from "./realTimeSync.service";
import { rmcService } from "./rmcService";
import { roomTypeService } from "./roomType.service";
import { zoneService } from "./zone.service";

export class CachedMasterDataService {
  /**
   * Get cities list with caching and real-time sync
   */
  async getCitiesList() {
    return realTimeCacheService.getOrFetch(CACHE_KEYS.CITIES, () =>
      citiesService.getCitiesByCountryId()
    );
  }

  /**
   * Get room types list with caching and real-time sync
   */
  async getRoomTypesList() {
    return realTimeCacheService.getOrFetch(CACHE_KEYS.ROOM_TYPES, () =>
      roomTypeService.getListItems()
    );
  }

  /**
   * Get property types list with caching and real-time sync
   */
  async getPropertyTypesList() {
    return realTimeCacheService.getOrFetch(CACHE_KEYS.PROPERTY_TYPES, () =>
      propertyTypeService.getAll()
    );
  }

  /**
   * Get RMC list with caching and real-time sync (full data)
   */
  async getRmcList() {
    return realTimeCacheService.getOrFetch(CACHE_KEYS.RMC_ALL, () =>
      rmcService.getAll()
    );
  }

  /**
   * Get RMC list items for dropdowns (lightweight - only id and name)
   */
  async getRmcListItems() {
    return realTimeCacheService.getOrFetch(CACHE_KEYS.RMC_LIST_ITEMS, () =>
      rmcService.getListItems()
    );
  }

  /**
   * Get zones list with caching and real-time sync
   */
  async getZonesList() {
    return realTimeCacheService.getOrFetch(CACHE_KEYS.ZONES, () =>
      zoneService.getAll()
    );
  }

  /**
   * Get clients by RMC ID with caching and real-time sync
   */
  async getClientsByRmcId(rmcId: number) {
    return realTimeCacheService.getOrFetch(
      `${CACHE_KEYS.CLIENT_BY_RMC}${rmcId}`,
      () => clientService.getByRMCId(rmcId)
    );
  }

  /**
   * Force refresh cities list and broadcast update
   */
  async refreshCitiesList() {
    const data = await realTimeCacheService.forceRefresh(
      CACHE_KEYS.CITIES,
      () => citiesService.getListItems()
    );

    // Broadcast the change to all components
    realTimeSyncService.broadcastMasterDataChange("cities", "update", "all", {
      data,
    });

    return data;
  }

  /**
   * Force refresh room types list and broadcast update
   */
  async refreshRoomTypesList() {
    const data = await realTimeCacheService.forceRefresh(
      CACHE_KEYS.ROOM_TYPES,
      () => roomTypeService.getListItems()
    );

    // Broadcast the change to all components
    realTimeSyncService.broadcastMasterDataChange(
      "room-types",
      "update",
      "all",
      { data }
    );

    return data;
  }

  /**
   * Force refresh property types list and broadcast update
   */
  async refreshPropertyTypesList() {
    const data = await realTimeCacheService.forceRefresh(
      CACHE_KEYS.PROPERTY_TYPES,
      () => propertyTypeService.getAll()
    );

    // Broadcast the change to all components
    realTimeSyncService.broadcastMasterDataChange(
      "property-types",
      "update",
      "all",
      { data }
    );

    return data;
  }

  /**
   * Force refresh RMC list (full data) and broadcast update
   */
  async refreshRmcList() {
    const data = await realTimeCacheService.forceRefresh(
      CACHE_KEYS.RMC_ALL,
      () => rmcService.getAll()
    );

    // Broadcast the change to all components
    realTimeSyncService.broadcast({
      type: "rmc",
      action: "update",
      entityId: "all",
      data: { data },
      timestamp: Date.now(),
    });

    return data;
  }

  /**
   * Force refresh RMC list items (for dropdowns) and broadcast update
   */
  async refreshRmcListItems() {
    const data = await realTimeCacheService.forceRefresh(
      CACHE_KEYS.RMC_LIST_ITEMS,
      () => rmcService.getListItems()
    );

    // Broadcast the change to all components
    realTimeSyncService.broadcast({
      type: "rmc",
      action: "update",
      entityId: "all",
      data: { data },
      timestamp: Date.now(),
    });

    return data;
  }

  /**
   * Force refresh zones list and broadcast update
   */
  async refreshZonesList() {
    const data = await realTimeCacheService.forceRefresh(CACHE_KEYS.ZONES, () =>
      zoneService.getAll()
    );

    // Broadcast the change to all components
    realTimeSyncService.broadcast({
      type: "master-data",
      action: "update",
      entityId: "zones",
      data: { data },
      timestamp: Date.now(),
    });

    return data;
  }

  /**
   * Force refresh clients by RMC ID and broadcast update
   */
  async refreshClientsByRmcId(rmcId: number) {
    const data = await realTimeCacheService.forceRefresh(
      `${CACHE_KEYS.CLIENT_BY_RMC}${rmcId}`,
      () => clientService.getByRMCId(rmcId)
    );

    // Broadcast the change to all components
    realTimeSyncService.broadcastClientChange("update", `rmc-${rmcId}`, {
      rmcId,
      data,
    });

    return data;
  }

  /**
   * Clear all cache and broadcast full refresh needed
   */
  async clearAndRefreshAll() {
    realTimeCacheService.clear();

    // Broadcast that everything needs refresh
    realTimeSyncService.broadcast({
      type: "master-data",
      action: "update",
      entityId: "all",
      data: { subType: "all", fullRefresh: true },
      timestamp: Date.now(),
    });
  }

  /**
   * Clear specific cache (legacy method for compatibility)
   */
  clearCache() {
    realTimeCacheService.clear();
  }

  clearCitiesCache() {
    realTimeCacheService.invalidate(CACHE_KEYS.CITIES);
  }

  clearRoomTypesCache() {
    realTimeCacheService.invalidate(CACHE_KEYS.ROOM_TYPES);
  }

  clearPropertyTypesCache() {
    realTimeCacheService.invalidate(CACHE_KEYS.PROPERTY_TYPES);
  }

  clearRmcCache() {
    realTimeCacheService.invalidate(CACHE_KEYS.RMC_ALL);
  }

  clearZonesCache() {
    realTimeCacheService.invalidate(CACHE_KEYS.ZONES);
  }

  clearClientsByRmcCache(rmcId: number) {
    realTimeCacheService.invalidate(`${CACHE_KEYS.CLIENT_BY_RMC}${rmcId}`);
  }

  clearAllClientCache() {
    realTimeCacheService.invalidateByPattern(
      new RegExp(`^${CACHE_KEYS.CLIENT_BY_RMC}`)
    );
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats() {
    return realTimeCacheService.getStats();
  }

  /**
   * Get real-time sync statistics for debugging
   */
  getSyncStats() {
    return {
      listenersCount: realTimeSyncService.getListenersCount(),
      listeners: realTimeSyncService.getListenersInfo(),
    };
  }
}

// Export singleton instance
export const cachedMasterDataService = new CachedMasterDataService();
